import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { InputText } from 'primeng/inputtext';
import { InputNumber } from 'primeng/inputnumber';
import { Select } from 'primeng/select';
import { Checkbox } from 'primeng/checkbox';
import { ButtonDirective } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { retry } from 'rxjs';
import { SectionCardComponent } from '../section-card/section-card';
import { MediaPickerComponent } from '../media-picker/media-picker';
import { AudioTrackEditorComponent } from '../audio-track-editor/audio-track-editor';
import { DimensionsEditorComponent } from '../dimensions-editor/dimensions-editor';
import { AttributesEditorComponent } from '../attributes-editor/attributes-editor';
import { VariantEditorComponent } from '../variant-editor/variant-editor';
import { SubtitleTrackEditorComponent } from '../subtitle-track-editor/subtitle-track-editor';
import { EditorFilesService } from '../../../features/editor/services/editor-files.service';
import { EditorConfigService } from '../../../features/editor/services/editor-config.service';
import { EditorJobsService } from '../../../features/editor/services/editor-jobs.service';
import { EditorDataService } from '../../../features/editor/services/editor-data.service';
import { EditorSlugsService } from '../../../features/editor/services/editor-slugs.service';
import { EditorStateService } from '../../../features/editor/services/editor-state.service';
import { ProjectsService } from '../../../core/services/projects.service';
import { AudioRef, ProjectData, FileCategory, SceneData, SubtitleEntry, SubtitleSourcesMap } from '../../models';
import { cloneDeep } from '../../utils/clone';
import { deriveNamesMapKey, deriveSlug, generateEntityId, makeUniqueSlug } from '../../utils/slug';
import { shouldStampVideoNp } from '../../utils/media-readiness';
import { setRefVariant, type VariantLike, type VariantRefLike } from '../../utils/variant-fallback';
import {
	CdkDragDrop,
	CdkDrag,
	CdkDropList,
	CdkDragHandle,
	CdkDragPlaceholder,
	moveItemInArray,
} from '@angular/cdk/drag-drop';

@Component({
	selector: 'app-project-form',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		FormsModule,
		InputText,
		InputNumber,
		Select,
		Checkbox,
		ButtonDirective,
		CdkDrag,
		CdkDropList,
		CdkDragHandle,
		CdkDragPlaceholder,
		SectionCardComponent,
		MediaPickerComponent,
		AudioTrackEditorComponent,
		DimensionsEditorComponent,
		AttributesEditorComponent,
		VariantEditorComponent,
		SubtitleTrackEditorComponent,
	],
	templateUrl: './project-form.html',
	styleUrl: './project-form.scss',
})
export class ProjectFormComponent {
	data = input.required<ProjectData>();
	dataChange = output<ProjectData>();
	chapterSelect = output<{ chapterSlug: string }>();
	imageSelect = output<{ chapterSlug: string | null; imageSlug: string }>();
	sceneSelect = output<{ chapterSlug: string | null; sceneSlug: string }>();
	copyScene = output<{ chapterSlug: null; sceneSlug: string }>();
	copyImage = output<{ chapterSlug: null; imageSlug: string }>();

	/** Project-level audio spans the whole project, so its start scene may live in any chapter. */
	protected sceneSlugs = computed(() => {
		const project = this.data();
		const slugs = (project?.scenes ?? []).map(s => s.slug);
		for (const chapter of project?.chapters ?? []) {
			for (const scene of chapter.scenes ?? []) {
				if (!slugs.includes(scene.slug)) slugs.push(scene.slug);
			}
		}
		return slugs;
	});

	protected patch(patcher: (draft: ProjectData) => void): void {
		const clone = cloneDeep(this.data() ?? ({} as ProjectData));
		patcher(clone);
		this.dataChange.emit(clone);
		this.stateService.projectData.set(clone);
	}

	// ── Settings ──────────────────────────────────────────────────────────────
	protected setName(val: string | null): void {
		this.patch(d => (d.name = val ?? ''));
	}
	protected setFps(val: number | null): void {
		this.patch(d => (d.fps = val ?? 25));
	}
	protected setOrigWidth(val: number | null): void {
		this.patch(d => (d.origCoordsWidth = val ?? 0));
	}
	protected setOrigHeight(val: number | null): void {
		this.patch(d => (d.origCoordsHeight = val ?? 0));
	}
	protected readonly audioSampleRateOptions = [
		{ label: '44.1 kHz', value: 44100 },
		{ label: '48 kHz', value: 48000 },
	];
	protected setAudioSampleRate(val: number | null): void {
		this.patch(d => (d.audioSampleRate = val ?? 44100));
	}

	// ── Chapters ──────────────────────────────────────────────────────────────
	protected onChapterClick(chapter: { slug: string }, event: MouseEvent): void {
		event.stopPropagation();
		this.chapterSelect.emit({ chapterSlug: chapter.slug });
	}

	protected addChapter(): void {
		const name = prompt('Chapter name:');
		if (!name?.trim()) return;
		const trimmed = name.trim();
		const existing = new Set((this.data()?.chapters ?? []).map(c => c.slug));
		const slug = makeUniqueSlug(deriveSlug(trimmed) || 'chapter', existing);
		this.patch(d => {
			if (!d.chapters) d.chapters = [];
			d.chapters.push({ id: generateEntityId(), name: trimmed, slug, scenes: [], images: [], audioTrack: [] });
		});
	}

	protected removeChapter(index: number, event: MouseEvent): void {
		event.stopPropagation();
		if (!confirm('Delete this chapter?')) return;
		const slug = this.data().chapters?.[index]?.slug;
		this.patch(d => {
			d.chapters?.splice(index, 1);
			// If no chapters are present, delete from json
			if (d.chapters?.length === 0) delete d.chapters;
		});
		if (slug) this.projectsService.deleteChapter(this.data().slug, slug).subscribe(() => this.slugsService.forgetChapter(slug));
	}

	protected dropChapter(event: CdkDragDrop<unknown[]>): void {
		this.patch(d => { if (d.chapters) moveItemInArray(d.chapters, event.previousIndex, event.currentIndex); });
	}

	// ── Project-level Images ──────────────────────────────────────────────────
	protected onImageClick(image: { slug: string }, event: MouseEvent): void {
		event.stopPropagation();
		this.imageSelect.emit({ chapterSlug: null, imageSlug: image.slug });
	}

	protected addImage(): void {
		const name = prompt('Image name:');
		if (!name?.trim()) return;
		const trimmed = name.trim();
		const existing = this.slugsService.takenImageSlugs(this.data()?.images ?? [], null);
		const s = makeUniqueSlug(deriveSlug(trimmed) || 'image', existing);
		const id = generateEntityId();
		this.patch(d => {
			if (!d.images) d.images = [];
			(d.images as unknown[]).push({ id, slug: s });
		});
		// Create default image doc in MongoDB — name lives on the doc
		const imagePath = `/projects/${this.data().slug}/images/${s}/image.json`;
		const defaultImage = { id, name: trimmed, slug: s, background: { source: { srcType: 'ABSOLUTE', dataType: 'IMAGE' } }, content: [] };
		this.dataService.saveFiles(new Map([[imagePath, defaultImage]]), 'add-image', { create: true }).pipe(retry({ count: 1, delay: 500 })).subscribe({
			error: (err) => {
				// The image.json write failed even after a retry — drop the ref we
				// just added so a broken reference (no matching image doc) never
				// reaches project.json. Without this, the player 404s on the
				// missing image at startup and crashes the whole preview.
				this.patch(d => { d.images = (d.images ?? []).filter((img: { slug: string }) => img.slug !== s); });
				this.messageService.add({
					severity: 'error', summary: 'Failed to add image',
					detail: err?.error?.error ?? 'Could not save the image content. Please try again.',
				});
			},
		});
	}

	protected removeImage(index: number, event: MouseEvent): void {
		event.stopPropagation();
		if (!confirm('Delete this image?')) return;
		const slug = this.data().images?.[index]?.slug;
		this.patch(d => { d.images?.splice(index, 1); });
		if (slug) this.projectsService.deleteImage(this.data().slug, slug).subscribe(() => this.slugsService.forgetImage(null, slug));
	}

	protected dropImage(event: CdkDragDrop<unknown[]>): void {
		this.patch(d => { if (d.images) moveItemInArray(d.images, event.previousIndex, event.currentIndex); });
	}

	// ── Project-level Scenes ──────────────────────────────────────────────────
	protected onSceneClick(scene: { slug: string }, event: MouseEvent): void {
		event.stopPropagation();
		this.sceneSelect.emit({ chapterSlug: null, sceneSlug: scene.slug });
	}

	private dataService = inject(EditorDataService);
	private projectsService = inject(ProjectsService);
	private filesService = inject(EditorFilesService);
	private stateService = inject(EditorStateService);
	private slugsService = inject(EditorSlugsService);
	private configService = inject(EditorConfigService);
	private jobsService = inject(EditorJobsService);
	private destroyRef = inject(DestroyRef);
	private messageService = inject(MessageService);

	protected addScene(): void {
		const name = prompt('Scene name:');
		if (!name?.trim()) return;
		const trimmed = name.trim();
		const existing = this.slugsService.takenSceneSlugs(this.data()?.scenes ?? [], null);
		const s = makeUniqueSlug(deriveSlug(trimmed) || 'scene', existing);
		const id = generateEntityId();
		this.patch(d => {
			if (!d.scenes) d.scenes = [];
			(d.scenes as unknown[]).push({ id, slug: s });
		});
		// Create default scene doc in MongoDB — name lives on the doc
		const scenePath = `/projects/${this.data().slug}/scenes/${s}/scene.json`;
		const defaultScene = { id, name: trimmed, slug: s, framesCount: 0, playbackType: 'VIDEO_NP', background: { source: { srcType: 'ABSOLUTE' } }, content: [] };
		this.dataService.saveFiles(new Map([[scenePath, defaultScene]]), 'add-scene', { create: true }).subscribe();
	}

	// ── Add scene from video file ───────────────────────────────────────────
	protected sceneVideoPickerVisible = signal(false);
	protected readonly sceneVideoPickerCategories: FileCategory[] = ['video'];

	protected openSceneVideoPicker(): void {
		this.sceneVideoPickerVisible.set(true);
	}

	protected onSceneVideosSelected(urls: string[]): void {
		const mediaByUrl = new Map(urls.map(u => [u, this.filesService.findByBaseUrl(u)] as const));
		const files = new Map<string, unknown>();
		const newScenes: { id: string; slug: string; variant?: { type: 'NAME'; key: string; namesMapKey?: string } }[] = [];
		const existingSlugs = this.slugsService.takenSceneSlugs(this.data()?.scenes ?? [], null);

		for (const baseUrl of urls) {
			const media = mediaByUrl.get(baseUrl);
			const rawName = media?.name ?? baseUrl.split('/').pop() ?? 'scene';
			const name = rawName.replace(/\.[^.]+$/, '');
			const slug = makeUniqueSlug(deriveSlug(name) || 'scene', existingSlugs);
			existingSlugs.add(slug);

			const fps = media?.fps || (this.data()?.fps ?? 25);
			const framesCount = media?.durationSeconds ? Math.round(media.durationSeconds * fps) : 0;
			const id = generateEntityId();

			if (baseUrl.includes('[VARIANT]')) {
				const namesMapKey = deriveNamesMapKey(media?.name ?? slug);
				newScenes.push({ id, slug, variant: { type: 'NAME', key: '', namesMapKey } });
			} else {
				newScenes.push({ id, slug });
			}
			// Scene doc carries name; ref does not
			const scenePath = `/projects/${this.data().slug}/scenes/${slug}/scene.json`;
			const sceneEntry = {
				id, name, slug, framesCount,
				// Only when a generation job will follow to clear it again (DEV-530).
				...(shouldStampVideoNp(media) ? { playbackType: 'VIDEO_NP' } : {}),
				background: { source: { src: baseUrl, srcType: 'ABSOLUTE', dataType: 'VIDEO' } },
				content: [],
			};
			files.set(scenePath, sceneEntry);
			// Seed sceneMap so the post-gen clearPlaybackTypeForMedia scan finds
			// these scenes — same pattern as editor.ts onTimelineFileDrop.
			this.stateService.setScene(scenePath, sceneEntry as unknown as SceneData);
		}
		if (!newScenes.length) return;

		this.patch(d => {
			if (!d.scenes) d.scenes = [];
			for (const s of newScenes) (d.scenes as unknown[]).push(s);
			// Populate namesMap for variant groups
			for (const baseUrl of urls) {
				if (!baseUrl.includes('[VARIANT]')) continue;
				const media = mediaByUrl.get(baseUrl);
				if (!media?.variants?.length) continue;
				const key = deriveNamesMapKey(media.name ?? 'variant');
				if (!key) continue;
				if (!d.namesMap) (d as Record<string, unknown>)['namesMap'] = {};
				(d.namesMap as Record<string, string[]>)[key] = media.variants.map(v => v.name);
			}
		});
		this.dataService.saveFiles(files, 'bulk-add-scenes', { create: true }).subscribe();

		// Auto-generate versions for each local video
		for (const baseUrl of urls) {
			const media = mediaByUrl.get(baseUrl);
			const mediaId = media?.id;
			if (media?.isLocal && mediaId && (media.versionCount ?? 0) <= 1) {
				this.filesService.generateVersions(mediaId)
					.pipe(this.jobsService.andTrack({ mediaId, mediaName: media.name ?? '', destroyRef: this.destroyRef }))
					.subscribe();
			}
		}
	}

	// ── Add scene from image file ───────────────────────────────────────────
	protected sceneImagePickerVisible = signal(false);
	protected readonly sceneImagePickerCategories: FileCategory[] = ['images'];

	protected openSceneImagePicker(): void {
		this.sceneImagePickerVisible.set(true);
	}

	protected onSceneImagesSelected(urls: string[]): void {
		const mediaByUrl = new Map(urls.map(u => [u, this.filesService.findByBaseUrl(u)] as const));
		const files = new Map<string, unknown>();
		const newScenes: { id: string; slug: string }[] = [];
		const existingSlugs = this.slugsService.takenSceneSlugs(this.data()?.scenes ?? [], null);

		for (const baseUrl of urls) {
			const media = mediaByUrl.get(baseUrl);
			const rawName = media?.name ?? baseUrl.split('/').pop() ?? 'scene';
			const name = rawName.replace(/\.[^.]+$/, '');
			const slug = makeUniqueSlug(deriveSlug(name) || 'scene', existingSlugs);
			existingSlugs.add(slug);

			const fps = media?.fps || (this.data()?.fps ?? 25);
			// Images have no probed duration — default to a 3s end-screen-style
			// hold, same as the drag&drop path in editor.ts onTimelineFileDrop.
			const framesCount = Math.round(3 * fps);
			const id = generateEntityId();

			newScenes.push({ id, slug });
			// Scene doc carries name; ref does not
			const scenePath = `/projects/${this.data().slug}/scenes/${slug}/scene.json`;
			const sceneEntry = {
				id, name, slug, framesCount,
				background: { source: { src: baseUrl, srcType: 'ABSOLUTE', dataType: 'IMAGE' } },
				content: [],
			};
			files.set(scenePath, sceneEntry);
			// Seed sceneMap so the post-gen clearPlaybackTypeForMedia scan finds
			// these scenes — same pattern as editor.ts onTimelineFileDrop.
			this.stateService.setScene(scenePath, sceneEntry as unknown as SceneData);
		}
		if (!newScenes.length) return;

		this.patch(d => {
			if (!d.scenes) d.scenes = [];
			for (const s of newScenes) (d.scenes as unknown[]).push(s);
		});
		this.dataService.saveFiles(files, 'bulk-add-scenes', { create: true }).subscribe();

		// Auto-generate versions for each local image
		for (const baseUrl of urls) {
			const media = mediaByUrl.get(baseUrl);
			const mediaId = media?.id;
			if (media?.isLocal && mediaId && (media.versionCount ?? 0) <= 1) {
				this.filesService.generateVersions(mediaId)
					.pipe(this.jobsService.andTrack({ mediaId, mediaName: media.name ?? '', destroyRef: this.destroyRef }))
					.subscribe();
			}
		}
	}

	// ── Add images from files ────────────────────────────────────────────────
	protected imagePickerVisible = signal(false);
	protected readonly imagePickerCategories: FileCategory[] = ['images'];

	protected openImagePicker(): void {
		this.imagePickerVisible.set(true);
	}

	protected onImagesSelected(urls: string[]): void {
		const mediaByUrl = new Map(urls.map(u => [u, this.filesService.findByBaseUrl(u)] as const));
		const files = new Map<string, unknown>();
		const newImages: { id: string; slug: string }[] = [];
		const existingSlugs = this.slugsService.takenImageSlugs(this.data()?.images ?? [], null);

		for (const baseUrl of urls) {
			const media = mediaByUrl.get(baseUrl);
			const rawName = media?.name ?? baseUrl.split('/').pop() ?? 'image';
			const name = rawName.replace(/\.[^.]+$/, '');
			const slug = makeUniqueSlug(deriveSlug(name) || 'image', existingSlugs);
			existingSlugs.add(slug);

			const id = generateEntityId();
			newImages.push({ id, slug });
			// Image doc carries name; ref does not
			const imagePath = `/projects/${this.data().slug}/images/${slug}/image.json`;
			files.set(imagePath, {
				id, name, slug,
				background: { source: { src: baseUrl, srcType: 'ABSOLUTE', dataType: 'IMAGE' } },
				content: [],
			});
		}
		if (!newImages.length) return;

		this.patch(d => {
			if (!d.images) d.images = [];
			for (const img of newImages) (d.images as unknown[]).push(img);
		});
		this.dataService.saveFiles(files, 'bulk-add-images', { create: true }).pipe(retry({ count: 1, delay: 500 })).subscribe({
			error: (err) => {
				// The image.json write failed even after a retry — drop the refs we
				// just added so a broken reference (no matching image doc) never
				// reaches project.json. Without this, the player 404s on the
				// missing image at startup and crashes the whole preview.
				const failedSlugs = new Set(newImages.map(img => img.slug));
				this.patch(d => {
					d.images = (d.images ?? []).filter((img: { slug: string }) => !failedSlugs.has(img.slug));
				});
				this.messageService.add({
					severity: 'error', summary: 'Failed to add image',
					detail: err?.error?.error ?? 'Could not save the image content. Please try again.',
				});
			},
		});

		// Auto-generate versions for each local image
		for (const baseUrl of urls) {
			const media = mediaByUrl.get(baseUrl);
			const mediaId = media?.id;
			if (media?.isLocal && mediaId && (media.versionCount ?? 0) <= 1) {
				this.filesService.generateVersions(mediaId)
					.pipe(this.jobsService.andTrack({ mediaId, mediaName: media.name ?? '', destroyRef: this.destroyRef }))
					.subscribe();
			}
		}
	}

	// ── Add audio tracks from files ─────────────────────────────────────────
	protected audioTrackPickerVisible = signal(false);
	protected readonly audioTrackPickerCategories: FileCategory[] = ['audios'];

	protected openAudioTrackPicker(): void {
		this.audioTrackPickerVisible.set(true);
	}

	protected onAudioTracksSelected(urls: string[]): void {
		const existingSlugs = new Set((this.data()?.audioTrack ?? []).map((a: { slug: string }) => a.slug));
		const newTracks: { id: string; name: string; slug: string; src: string; srcType: string }[] = [];
		for (const baseUrl of urls) {
			const media = this.filesService.findByBaseUrl(baseUrl);
			const rawName = media?.name ?? baseUrl.split('/').pop() ?? 'audio';
			const name = rawName.replace(/\.[^.]+$/, '');
			const slug = makeUniqueSlug(deriveSlug(name) || 'audio', existingSlugs);
			existingSlugs.add(slug);
			newTracks.push({ id: generateEntityId(), name, slug, src: baseUrl, srcType: 'ABSOLUTE' });
		}
		this.patch(d => {
			if (!d.audioTrack) d.audioTrack = [];
			for (const t of newTracks) (d.audioTrack as unknown[]).push(t);
		});
	}

	protected requestCopyScene(index: number, event: MouseEvent): void {
		event.stopPropagation();
		const slug = this.data().scenes?.[index]?.slug;
		if (slug) this.copyScene.emit({ chapterSlug: null, sceneSlug: slug });
	}

	protected requestCopyImage(index: number, event: MouseEvent): void {
		event.stopPropagation();
		const slug = this.data().images?.[index]?.slug;
		if (slug) this.copyImage.emit({ chapterSlug: null, imageSlug: slug });
	}

	protected removeScene(index: number, event: MouseEvent): void {
		event.stopPropagation();
		if (!confirm('Delete this scene?')) return;
		const slug = this.data().scenes?.[index]?.slug;
		this.patch(d => { d.scenes?.splice(index, 1); });
		if (slug) this.projectsService.deleteScene(this.data().slug, slug).subscribe(() => this.slugsService.forgetScene(null, slug));
	}

	protected dropScene(event: CdkDragDrop<unknown[]>): void {
		this.patch(d => { if (d.scenes) moveItemInArray(d.scenes, event.previousIndex, event.currentIndex); });
	}

	// ── Expand/attributes on scene+image refs ─────────────────────────────────
	protected expandedScene = signal<number | null>(null);
	protected expandedImage = signal<number | null>(null);

	protected toggleSceneExpand(index: number, event: MouseEvent): void {
		event.stopPropagation();
		this.expandedScene.update(v => v === index ? null : index);
	}

	protected toggleImageExpand(index: number, event: MouseEvent): void {
		event.stopPropagation();
		this.expandedImage.update(v => v === index ? null : index);
	}

	protected setSceneAttributes(index: number, attrs: Record<string, unknown>): void {
		this.patch(d => {
			const scene = d.scenes?.[index] as Record<string, unknown> | undefined;
			if (!scene) return;
			const keys = Object.keys(attrs);
			if (keys.length > 0) {
				scene['attributes'] = attrs;
				scene['groups'] = keys;
			} else {
				delete scene['attributes'];
				delete scene['groups'];
			}
		});
	}

	protected setSceneVariant(index: number, variant: VariantLike | null): void {
		this.patch(d => {
			const scenes = (d.scenes ?? []) as unknown as VariantRefLike[];
			const scene = scenes[index];
			if (scene) setRefVariant(scenes, scene, variant);
		});
	}

	protected setSceneAlt(index: number, alt: boolean): void {
		this.patch(d => {
			const scene = d.scenes?.[index] as unknown as VariantRefLike | undefined;
			if (!scene) return;
			if (alt) scene.alt = true;
			else delete scene.alt;
		});
	}

	protected getSceneSlugs(): string[] {
		return (this.data()?.scenes ?? []).map((s: { slug: string }) => s.slug);
	}

	protected getNamesMapKeys(): string[] {
		const nm = this.data()?.namesMap;
		return nm ? Object.keys(nm) : [];
	}

	protected getArgKeys(): string[] {
		return this.configService.argDefs().map(d => d.key);
	}

	protected onAudioTrackChange(tracks: AudioRef[]): void {
		const oldLen = (this.data().audioTrack ?? []).length;
		const added = tracks.length > oldLen;
		this.patch(d => {
			d.audioTrack = tracks;
			if (!added) return;
			// Auto-populate namesMap for variant groups in one atomic patch
			for (const track of tracks) {
				if (!track.src?.includes('[VARIANT]')) continue;
				const media = this.filesService.findByBaseUrl(track.src);
				if (!media?.variants?.length) continue;
				const key = deriveNamesMapKey(media.name ?? 'variant');
				if (!key) continue;
				if (!d.namesMap) d.namesMap = {};
				(d.namesMap as Record<string, string[]>)[key] = media.variants.map(v => v.name);
			}
		});
	}

	protected onNamesMapPopulate(event: { key: string; names: string[] }): void {
		this.patch(d => {
			if (!d.namesMap) d.namesMap = {};
			(d.namesMap as Record<string, string[]>)[event.key] = event.names;
		});
	}

	protected setHideSubtitlesButton(val: boolean): void {
		this.patch(d => {
			if (val) d.hideSubtitlesButton = true;
			else delete d.hideSubtitlesButton;
		});
	}

	protected setClickableThumbnail(val: boolean): void {
		this.patch(d => {
			if (val) d.clickableThumbnail = true;
			else delete d.clickableThumbnail;
		});
	}

	protected onSubtitlesChange(subtitles: SubtitleEntry[]): void {
		this.patch(d => {
			d.subtitles = subtitles;
			// Prune subtitleSources for tracks that no longer exist, so removing a
			// track doesn't leave orphaned file mappings behind. Done here (not in
			// the track editor) so the array change and the cleanup land in ONE
			// patch — two patch() calls in one synchronous handler would clobber
			// each other, since `data` input propagation lags behind the emit.
			const sources = d.__platformContent?.subtitleSources;
			if (sources) {
				const liveSlugs = new Set(subtitles.map(t => t.slug));
				for (const slug of Object.keys(sources)) {
					if (!liveSlugs.has(slug)) delete sources[slug];
				}
			}
		});
	}

	protected onSubtitleSourcesChange(sources: SubtitleSourcesMap): void {
		this.patch(d => {
			if (!d.__platformContent) d.__platformContent = {};
			d.__platformContent.subtitleSources = sources;
		});
	}

	protected setImageAttributes(index: number, attrs: Record<string, unknown>): void {
		this.patch(d => {
			const image = d.images?.[index] as Record<string, unknown> | undefined;
			if (!image) return;
			const keys = Object.keys(attrs);
			if (keys.length > 0) {
				image['attributes'] = attrs;
				image['groups'] = keys;
			} else {
				delete image['attributes'];
				delete image['groups'];
			}
		});
	}

	// ── Default Arguments / Variables ─────────────────────────────────────────
	protected mapEntries(field: string): { key: string; value: string | number | boolean }[] {
		const m = this.data()?.[field] ?? {};
		return Object.entries(m).map(([key, value]) => ({ key, value: value as string | number | boolean }));
	}
	protected addMapEntry(field: string): void {
		this.patch(d => {
			if (!d[field]) d[field] = {};
			const map = d[field] as Record<string, unknown>;
			let key = 'new_key';
			let n = 1;
			while (map[key]) key = `new_key_${n++}`;
			map[key] = '';
		});
	}
	protected removeMapEntry(field: string, key: string): void {
		this.patch(d => { delete (d[field] as Record<string, unknown>)[key]; });
	}
	protected renameMapEntry(field: string, oldKey: string, newKey: string): void {
		if (oldKey === newKey || !newKey.trim()) return;
		this.patch(d => {
			const map = d[field] as Record<string, unknown>;
			const val = map[oldKey];
			delete map[oldKey];
			map[newKey.trim()] = val;
		});
	}
	protected setMapEntryValue(field: string, key: string, text: string): void {
		this.patch(d => { (d[field] as Record<string, unknown>)[key] = text; });
	}

}
